import type {
  Aspect,
  AuthState,
  JsonValue,
  LayoutId,
  Presentation,
  PresentationDetails,
  PublicPresentation,
  Revision,
  SlidePatchOperation,
  Theme,
} from './types'

let csrfToken = ''

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code = 'REQUEST_FAILED',
    readonly details: string[] = [],
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export function setCsrfToken(value?: string): void {
  csrfToken = value ?? ''
}

function isMutation(method: string): boolean {
  return !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const method = init.method ?? 'GET'
  const headers = new Headers(init.headers)
  if (
    init.body !== undefined
    && !(init.body instanceof FormData)
    && !headers.has('content-type')
  ) {
    headers.set('content-type', 'application/json')
  }
  if (csrfToken && isMutation(method)) headers.set('x-csrf-token', csrfToken)

  const response = await fetch(path, {
    ...init,
    method,
    headers,
    credentials: 'same-origin',
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as {
      error?: string
      code?: string
      details?: string[]
    }
    throw new ApiError(
      body.error ?? 'Не удалось выполнить запрос.',
      response.status,
      body.code,
      body.details,
    )
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

function json(method: string, body: unknown): RequestInit {
  return { method, body: JSON.stringify(body) }
}

export const authApi = {
  me: () => request<AuthState>('/api/auth/me'),
  initData: (initData: string) =>
    request<AuthState>('/api/auth/init-data', json('POST', { initData })),
  start: () =>
    request<{
      status: 'pending'
      nonce: string
      botUrl: string
      expiresAt: string
    }>('/api/auth/start', json('POST', {})),
  poll: (nonce: string) =>
    request<
      | { status: 'pending' | 'expired' | 'used' }
      | (AuthState & { status: 'authenticated' })
    >('/api/auth/poll', json('POST', { nonce })),
  logout: () => request<{ ok: true }>('/api/auth/logout', json('POST', {})),
}

export const presentationsApi = {
  list: (limit = 50, offset = 0) =>
    request<{ items: Presentation[]; limit: number; offset: number }>(
      `/api/presentations?limit=${limit}&offset=${offset}`,
    ),
  get: (id: string) =>
    request<PresentationDetails>(`/api/presentations/${encodeURIComponent(id)}`),
  create: (input: { title: string; aspect: Aspect; theme?: Theme }) =>
    request<{
      presentation: Presentation
      revision: Revision
      rendering: boolean
    }>('/api/presentations', json('POST', input)),
  generate: (
    id: string,
    input: {
      baseRevision: number
      description: string
      slideCount: number
      theme?: Theme | { id: 'auto'; description: string }
      audience?: string
      tone?: string
    },
  ) =>
    request<{
      presentation: Presentation
      revision: Revision
      rendering: boolean
      outline: unknown
    }>(`/api/presentations/${encodeURIComponent(id)}/generate`, json('POST', input)),
  patchSlide: (
    id: string,
    slideId: string,
    baseRevision: number,
    ops: SlidePatchOperation[],
  ) =>
    request<{ revision: Revision; changedSlideNumber: number; rendering: boolean }>(
      `/api/presentations/${encodeURIComponent(id)}/slides/${encodeURIComponent(slideId)}`,
      json('PATCH', { baseRevision, ops }),
    ),
  aiEditSlide: (
    id: string,
    slideId: string,
    baseRevision: number,
    instruction: string,
  ) =>
    request<{ revision: Revision; changedSlideNumber: number; rendering: boolean }>(
      `/api/presentations/${encodeURIComponent(id)}/slides/${encodeURIComponent(slideId)}/ai-edit`,
      json('POST', { baseRevision, instruction }),
    ),
  changeLayout: (
    id: string,
    slideId: string,
    baseRevision: number,
    layout: LayoutId,
    content: Record<string, unknown>,
  ) =>
    request<{ revision: Revision; changedSlideNumber: number; rendering: boolean }>(
      `/api/presentations/${encodeURIComponent(id)}/slides/${encodeURIComponent(slideId)}/layout`,
      json('PATCH', { baseRevision, layout, content }),
    ),
  changeTheme: (
    id: string,
    baseRevision: number,
    theme: Theme | { id: 'auto'; description: string },
  ) =>
    request<{ revision: Revision; rendering: boolean }>(
      `/api/presentations/${encodeURIComponent(id)}/theme`,
      json('PATCH', { baseRevision, theme }),
    ),
  changeAspect: (id: string, baseRevision: number, aspect: Aspect) =>
    request<{ revision: Revision; rendering: boolean }>(
      `/api/presentations/${encodeURIComponent(id)}/aspect`,
      json('PATCH', { baseRevision, aspect }),
    ),
  uploadImage: (
    id: string,
    slideId: string,
    baseRevision: number,
    file: File,
    alt?: string,
  ) => {
    const data = new FormData()
    data.append('image', file)
    const query = new URLSearchParams({ baseRevision: String(baseRevision) })
    if (alt?.trim()) query.set('alt', alt.trim())
    return request<{
      revision: Revision
      changedSlideNumber: number
      image: { path: string; bytes: number; mimeType: string }
      rendering: boolean
    }>(
      `/api/presentations/${encodeURIComponent(id)}/slides/${encodeURIComponent(slideId)}/image?${query}`,
      { method: 'POST', body: data },
    )
  },
  publish: (id: string, revision?: number) =>
    request<{ published: true; revision: Revision; publicUrl: string }>(
      `/api/presentations/${encodeURIComponent(id)}/publish`,
      json('POST', revision === undefined ? {} : { revision }),
    ),
  undo: (id: string, baseRevision: number) =>
    request<{ revision: Revision; rendering: boolean }>(
      `/api/presentations/${encodeURIComponent(id)}/undo`,
      json('POST', { baseRevision }),
    ),
}

export const publicApi = {
  get: (slug: string) =>
    request<PublicPresentation>(
      `/api/public/presentations/${encodeURIComponent(slug)}`,
      { cache: 'no-store' },
    ),
  createClicker: (slug: string) =>
    request<{
      roomCode: string
      controllerUrl: string
      controllerExpiresAt: string
      screenToken: string
      wsPath: string
      revision: number
      slideIndex: number
      seq: number
    }>(
      `/api/public/presentations/${encodeURIComponent(slug)}/clicker`,
      json('POST', {}),
    ),
  connectController: (credential: { token: string } | { code: string }) =>
    request<{
      slug: string
      controllerToken: string
      revision: number
      slideIndex: number
      seq: number
      notes: string[]
    }>('/api/clicker/controller', json('POST', credential)),
  resumeController: (token: string) =>
    request<{
      slug: string
      revision: number
      slideIndex: number
      seq: number
      notes: string[]
    }>('/api/clicker/controller/resume', json('POST', { token })),
}

export interface AdminOverview {
  presentations: Array<{
    id: string
    slug: string
    title: string
    aspect: Aspect
    themeId: string
    status: string
    slideCount: number
    draftRevision: number | null
    publishedRevision: number | null
    owner: {
      id: string
      telegramId: string | null
      username: string | null
      firstName: string | null
    }
    publicUrl: string | null
    createdAt: string
    updatedAt: string
  }>
  ai: {
    calls: number
    failed: number
    tokensIn: number
    tokensOut: number
    totalMs: number
    groups: Array<{
      stage: string
      provider?: string
      model?: string
      calls: number
      tokensIn: number
      tokensOut: number
      failed: number
      totalMs?: number
    }>
  }
  queue: {
    statuses: Array<{
      status: string
      jobs: number
      oldestAt: string | null
    }>
    recent: Array<{
      id: string
      kind: string
      status: string
      attempts: number
      maxAttempts: number
      lastError: string | null
      createdAt: string
      updatedAt: string
    }>
  }
}

export const adminApi = {
  overview: () => request<AdminOverview>('/api/admin/overview'),
}

export function asJsonValue(value: unknown): JsonValue {
  return value as JsonValue
}
